// The mixer's control plane (P4.10) — the subscriber that turns bus events into engine commands.
//
// ⚠️ **Every mixer control publishes a `ControlEvent`; none of them touches the engine directly.**
// P4.10's rule, and the reason is FF-1: *"MIDI learn is a source on that bus, and a control wired
// directly to the engine is a control MIDI can never reach."* A knob calling `graph.setTrim` from its
// `onChange` would work perfectly today and be unmappable forever.
//
// So this hook is the *one* place a mixer control event becomes sound, exactly as `useTrackLoader`'s
// subscriber is for the deck. Architecture §3 calls this the point of consumption, and it is where
// the bus splits: `toMixerCommand` returns null for anything that is not the mixer's, the same way
// `toEngineCommand` and `toTransportEvent` return null for each other's events.
//
// ## Why the positions live in React and the values do not
//
// A knob has to draw itself, so its 0…1 position is React state — it changes when a hand moves it,
// which is exactly when a re-render is wanted. What must *not* go through React is anything read
// from a SAB at display rate, which for the mixer is the meters (P4.7). Stack §2's rule, and the
// reason `Transport.tsx` reads its own buffer inside its own rAF loop.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { AudioGraph } from '../../audio/engine';
import { controlEvent, type ControlBus } from '../../control';
import {
  MIXER_CONTROL,
  MIXER_MASTER_CONTROL,
  toMixerCommand,
} from '../../audio/engine/control-mapping';
import type { ControlId, DeckId } from '../../platform/types/control';
import {
  CROSSFADER_CENTRE,
  CROSSFADER_CURVE,
  MASTER_GAIN_UNITY_POSITION,
  TRIM_UNITY_POSITION,
  type CrossfaderCurve,
} from '../../platform/types/mixer';

/** One strip's knob positions, 0…1, as the UI holds them. */
export interface StripPositions {
  readonly trim: number;
  readonly eqLow: number;
  readonly eqMid: number;
  readonly eqHigh: number;
  readonly filter: number;
  /** FR-15's channel fader (P4.5). 1 is fully open — see `NEUTRAL`. */
  readonly fader: number;
  /** FR-17's FX slot 0 (P4.8): the unit toggle, dry/wet, division and feedback, all as positions. */
  readonly fxUnit: number;
  readonly fxMix: number;
  readonly fxDivision: number;
  readonly fxFeedback: number;
  /** FR-17's reverb (P4.9). Separate from the echo's two, so switching unit keeps both settings. */
  readonly fxDecay: number;
  readonly fxDamping: number;
}

export interface MixerApi {
  readonly positions: Record<DeckId, StripPositions>;
  /** The crossfader, 0…1 (P4.5). `CROSSFADER_CENTRE` is both decks. */
  readonly crossfader: number;
  /** Which of FR-15's two curves the crossfader is using. */
  readonly crossfaderCurve: CrossfaderCurve;
  /** FR-16's master gain, 0…1 (P4.6). `MASTER_GAIN_UNITY_POSITION` is unity. */
  readonly masterGain: number;
  /** Moves a control. Publishes; does not apply. */
  readonly move: (id: ControlId, position: number) => void;
  /** Every control id this surface owns, for the cheat sheet and for FR-39's remapping UI. */
  readonly controlIds: readonly ControlId[];
}

/** Centre on every knob: trim at unity, every band at unity. */
const NEUTRAL: StripPositions = {
  trim: TRIM_UNITY_POSITION,
  eqLow: TRIM_UNITY_POSITION,
  eqMid: TRIM_UNITY_POSITION,
  eqHigh: TRIM_UNITY_POSITION,
  // Centre is the detent, which is a real bypass — see `filter.ts`.
  filter: TRIM_UNITY_POSITION,
  // ⚠️ **Open, not centred**, and it is the one control here whose neutral is not the middle. A
  // fader's neutral is unity, which is the top of its travel; starting it at 0.5 would make every
  // channel 6 dB quiet until someone noticed, and "the mixer is quiet" is a bug nobody attributes
  // to a default.
  fader: 1,
  // ⚠️ **Off, and dry.** An FX slot that started loaded would put an echo on the first track
  // anybody played, which is the same class of surprise as a mixer whose channels start closed —
  // just louder. The division and feedback below are where a knob *sits*, not what it does, so they
  // are free to be musical defaults: a 1/2-beat echo at moderate feedback is the one a hand reaches
  // for first, and it is inaudible until the wet knob leaves zero.
  fxUnit: 0,
  fxMix: 0,
  fxDivision: 1 / 3,
  fxFeedback: 0.4,
  // A medium hall and moderate damping: the setting a hand reaches for first, and inaudible until
  // the wet knob leaves zero.
  fxDecay: 0.45,
  fxDamping: 0.5,
};

/** Which knob an id belongs to, so one publish path updates the right position. */
const FIELD_BY_ID = new Map<string, keyof StripPositions>([
  [MIXER_CONTROL.a.TRIM, 'trim'],
  [MIXER_CONTROL.a.EQ_LOW, 'eqLow'],
  [MIXER_CONTROL.a.EQ_MID, 'eqMid'],
  [MIXER_CONTROL.a.EQ_HIGH, 'eqHigh'],
  [MIXER_CONTROL.a.FILTER, 'filter'],
  [MIXER_CONTROL.a.FADER, 'fader'],
  [MIXER_CONTROL.a.FX_UNIT, 'fxUnit'],
  [MIXER_CONTROL.a.FX_MIX, 'fxMix'],
  [MIXER_CONTROL.a.FX_DIVISION, 'fxDivision'],
  [MIXER_CONTROL.a.FX_FEEDBACK, 'fxFeedback'],
  [MIXER_CONTROL.a.FX_DECAY, 'fxDecay'],
  [MIXER_CONTROL.a.FX_DAMPING, 'fxDamping'],
  [MIXER_CONTROL.b.TRIM, 'trim'],
  [MIXER_CONTROL.b.EQ_LOW, 'eqLow'],
  [MIXER_CONTROL.b.EQ_MID, 'eqMid'],
  [MIXER_CONTROL.b.EQ_HIGH, 'eqHigh'],
  [MIXER_CONTROL.b.FILTER, 'filter'],
  [MIXER_CONTROL.b.FADER, 'fader'],
  [MIXER_CONTROL.b.FX_UNIT, 'fxUnit'],
  [MIXER_CONTROL.b.FX_MIX, 'fxMix'],
  [MIXER_CONTROL.b.FX_DIVISION, 'fxDivision'],
  [MIXER_CONTROL.b.FX_FEEDBACK, 'fxFeedback'],
  [MIXER_CONTROL.b.FX_DECAY, 'fxDecay'],
  [MIXER_CONTROL.b.FX_DAMPING, 'fxDamping'],
]);

const DECK_BY_ID = new Map<string, DeckId>([
  ...Object.values(MIXER_CONTROL.a).map((id): [string, DeckId] => [id, 'a']),
  ...Object.values(MIXER_CONTROL.b).map((id): [string, DeckId] => [id, 'b']),
]);

export const MIXER_CONTROL_IDS: readonly ControlId[] = [
  ...Object.values(MIXER_CONTROL.a),
  ...Object.values(MIXER_CONTROL.b),
  // The mixer's own two, which belong to no deck. `mixer-controls.test.ts` asserts this list and
  // the mapping table name the same controls, and it caught their absence the moment the table had
  // them and this did not.
  ...Object.values(MIXER_MASTER_CONTROL),
];

export function useMixer(bus: ControlBus, graph: AudioGraph | null): MixerApi {
  const [positions, setPositions] = useState<Record<DeckId, StripPositions>>({
    a: NEUTRAL,
    b: NEUTRAL,
  });
  const [crossfader, setCrossfader] = useState(CROSSFADER_CENTRE);
  const [crossfaderCurve, setCrossfaderCurve] = useState<CrossfaderCurve>(
    CROSSFADER_CURVE.SMOOTH,
  );
  const [masterGain, setMasterGain] = useState(MASTER_GAIN_UNITY_POSITION);

  /**
   * The graph, **handed in already open** — this hook never opens one.
   *
   * ⚠️⚠️ **It used to, and the comment here said it did not.** The first version of this file
   * carried exactly this warning — "never opened from here… the decks own that gesture" — and eleven
   * lines below it called `openGraph()` in a mount effect, creating the `AudioContext` at page load
   * outside any user gesture. A context created that way starts **suspended**, and every deck built
   * on it inherits that.
   *
   * CI found it, not review: the Sync phase test went from ≤10 ms to **31.3 ms** — the one assertion
   * in the suite sensitive to the context's own timing. A comment asserting a property its own file
   * does not have is the shape this repo has now shipped eight times, and this one was written in
   * the same commit as the warning against it.
   *
   * So the type is the fix rather than the discipline: `AudioGraph | null` cannot open anything. A
   * moved knob before a track is loaded updates its own position and reaches nothing, which is the
   * honest behaviour — there is no audio to trim yet.
   */
  const graphRef = useRef<AudioGraph | null>(graph);
  graphRef.current = graph;

  useEffect(() => {
    // Subscribed rather than polled, and the subscription is what makes a keyboard, a pointer and
    // (at FF-1) a MIDI controller indistinguishable from here.
    // Subscribed to the whole `mixer` namespace rather than to eight ids. `controlIdInNamespace`
    // matches on segment boundaries, so this takes `mixer.a.trim` and would take `mixer.crossfader`
    // when P4.5 adds it — one subscription that does not need editing every time a control lands.
    const unsubscribe = bus.subscribe('mixer', (event) => {
      const field = FIELD_BY_ID.get(event.id);
      const deck = DECK_BY_ID.get(event.id);
      if (field !== undefined && deck !== undefined) {
        setPositions((current) => ({
          ...current,
          [deck]: { ...current[deck], [field]: event.value },
        }));
      }

      if (event.id === MIXER_MASTER_CONTROL.MASTER_GAIN) {
        setMasterGain(event.value);
      } else if (event.id === MIXER_MASTER_CONTROL.CROSSFADER) {
        setCrossfader(event.value);
      } else if (event.id === MIXER_MASTER_CONTROL.CROSSFADER_CURVE) {
        // The same threshold the mapping table applies, and deliberately not a second rule: the
        // readout must name the curve the engine is actually using, so both sides round at 0.5.
        setCrossfaderCurve(
          event.value >= 0.5 ? CROSSFADER_CURVE.SHARP : CROSSFADER_CURVE.SMOOTH,
        );
      }

      const command = toMixerCommand(event.id, event.value);
      if (command === null) {
        return;
      }
      const graph = graphRef.current;
      if (graph === null) {
        return;
      }
      graph.sendMixerCommand(command);
    });

    return unsubscribe;
  }, [bus]);

  const move = useCallback(
    (id: ControlId, position: number): void => {
      // `pointer` is the source; a keyboard arrow on the same `<input type="range">` is still a
      // pointer-class gesture on this control as far as the bus is concerned, because it is the same
      // element producing the same continuous value. FF-1's MIDI events will arrive with `midi` and
      // travel the identical path from here.
      bus.publish(controlEvent(id, position, 0, 'pointer'));
    },
    [bus],
  );

  return {
    positions,
    crossfader,
    crossfaderCurve,
    masterGain,
    move,
    controlIds: MIXER_CONTROL_IDS,
  };
}
